// vzhlad_predajne_v2 (20. 10. 2026, ústava 61.10): starý oranžový panel/akcent nahradený hodnotami témy — 1 miest
// DennikView.jsx — V2 DESKTOP DENNÍK (dohľad + zápis + PDF kniha)
// ════════════════════════════════════════════════════════════════════
// Ústava: 25 (denník) · 4.A (typy + koniec etapy) · 5 (roly) · 9.D (postup) ·
//         53.7 lock · 53.1 dirty · 12.0/3 Form→Confirm · 56 master-detail · 26 A4.
// Pre-flight: PREFLIGHT_DENNIK_DESKTOP_V2.md (D1–D7, schválené Mirkom).
//
// JEDEN denník, dve zobrazenia — tá istá `diary_entries` + `/api/diary` ako mobil.
// Layout: ľavý = zákazky · stredný = vlákno zápisov · pravý = detail zápisu.
// DENNIK_TUK_V1 (21. 10. 2026, ksbx-appky 2849): office vidí prestoj s dôvodom, „kto zavinil“ a fotkou (štítok PRESTOJ,
// CHÝBA FOTKA, filter S prestojom), práce z katalógu denníka (GET /api/diary/katalog, jeden katalóg s mview a appkami);
// prestoj upravuje výberom z katalógu. KPI karty preč — počty v hlavičke a vo filtroch (61.10, pracovná plocha na výšku).

import HelpHint from '../components/HelpHint.jsx'   // hinty_v1: obrazovka nemala ani jednu nápovedu
import React, { useState, useMemo, useEffect, useCallback } from 'react'
import useLiveRefresh from '../hooks/useLiveRefresh.js'
import { COLORS, TYPO, SPACE, RADIUS, TINT, todayLocal, hladaj } from '../theme.js'
import {
  PageHeader, MasterDetail, ListRow, ListStatusBar,
  FilterChips, Tabs, EmptyPane, SearchableSelect,
} from '../components/Ui.jsx'
import { Modal } from '../components/Modal.jsx'
import { Btn, BtnAdd, BtnGhost } from '../components/Btn.jsx'
import RowMenu from '../components/RowMenu.jsx'
import { HistoryPanel } from '../components/HistoryPanel.jsx'
import { useToast } from '../components/Toast.jsx'
import { useShortcuts } from '../hooks/useShortcuts.js'
import { useListKeyboard } from '../hooks/useListKeyboard.js'
import { useRecordEditor } from '../hooks/useRecordEditor.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { usePersony } from '../lib/persony.js'
import { bytNazov, bytMaSlovo } from '../lib/byt.js'
import DennikKniha from './DennikKniha.jsx'
import PoterProtokolView from './PoterProtokolView.jsx'

const ALL = '__all__'   // pseudo-zákazka „Všetky zápisy"

// ── typy zápisu — kanonické + legacy normalizácia (ústava 4.A) ───────
const TYPE_MAP = {
  first: 'prvy', start: 'prvy', daily: 'denny', inspection: 'kontrolny',
  supervisor: 'kontrolny', last: 'posledny', end: 'posledny', handover: 'posledny',
  partial: 'priebezny', interim: 'priebezny',
}
function normType(t) {
  if (['prvy', 'denny', 'kontrolny', 'posledny', 'priebezny', 'poter_protokol'].includes(t)) return t
  return TYPE_MAP[t] || 'denny'
}
const TYPES = {
  denny:     { label: 'Denný',     color: COLORS.nav },
  prvy:      { label: 'Prvý',      color: COLORS.info },
  kontrolny: { label: 'Kontrolný', color: COLORS.warn },
  posledny:  { label: 'Posledný',  color: COLORS.muted },
  priebezny: { label: 'Priebežný', color: COLORS.muted },
  poter_protokol: { label: 'Protokol', color: COLORS.add },
}
// Poterový protokol NEZAKLADÁ zápis do diary_entries (ústava 4.A) — v zozname sa zobrazí LEN vizuálne,
// klik otvorí PDF, nie ZapisModal (Mirko 3.8. „nevytvára sa zápis do denníka, ale zobrazuje sa v denníku")
function protokolToRow(p) {
  return {
    id: 'PP_' + p.id, project_id: p.project_id, project_name: p.project_name || '',
    date: (p.created_at || '').slice(0, 10), entry_type: 'poter_protokol',
    work_description: 'Poterový protokol' + (p.material_name ? ' — ' + p.material_name : '')
      + (p.celkovo_m3 ? ' · ' + p.celkovo_m3 + ' m³' : ''),
    created_by: p.created_by, created_at: p.created_at, photo_count: 0, koniec_etapy: 0,
    _isProtokol: true, _pdfFile: p.pdf_file, _protokolId: p.id,
  }
}
const WEATHER = ['sucho', 'zamracene', 'dazd', 'sneh', 'vietor', 'mraz']
const WEATHER_LABEL = { sucho: 'Sucho', zamracene: 'Zamračené', dazd: 'Dážď',
  sneh: 'Sneh', vietor: 'Vietor', mraz: 'Mráz' }
const FLOORS = ['Prízemie', '1.NP', '2.NP', '3.NP', 'Garáž', 'Terasa', 'Pivnica', 'Exteriér']
// DENNIK_TUK_V1: dôvody prestoja idú z katalógu denníka; tento zoznam ostáva len pre staré zápisy bez kľúča
const STOPPAGE_REASONS = ['Nepripravenosť', 'Tech. porucha', 'Nie je materiál',
  'Nevhodná teplota', 'Neporiadok', 'Vlastný']

const DATE_FILTERS = [
  { key: 'all', label: 'Všetko', color: COLORS.muted },
  { key: 'today', label: 'Dnes', color: COLORS.nav },
  { key: 'yesterday', label: 'Včera', color: COLORS.nav },
  { key: 'week', label: 'Tento týždeň', color: COLORS.nav },
]
function isControlRole(r) { return ['foreman', 'owner', 'owner_view', 'admin'].includes(r) }
// typy zápisu povolené pre rolu (ústava sek. 4 rola-matica)
function allowedTypes(role) {
  if (role === 'admin') return ['denny', 'prvy', 'kontrolny', 'posledny']
  // DENNIK_TUK_V1 — Mirko 29. 9. 2026 (matrix 1007): „Majitel, admin, Majster po novom technik - Prvý zápis, a denné zápisy“
  if (['foreman', 'owner', 'owner_view'].includes(role)) return ['denny', 'prvy', 'kontrolny', 'posledny']
  if (role === 'office_manager') return ['denny']
  return []
}
function today() { return todayLocal() }
// dátumový rozsah pre preset filtre (dnes / včera / tento týždeň)
function dateRange(fl) {
  if (fl === 'all') return null
  const iso = d => d.toISOString().slice(0, 10)
  const now = new Date()
  if (fl === 'today') { const t = iso(now); return [t, t] }
  if (fl === 'yesterday') { const y = new Date(now); y.setDate(y.getDate() - 1); return [iso(y), iso(y)] }
  if (fl === 'week') { const m = new Date(now); m.setDate(m.getDate() - ((m.getDay() + 6) % 7)); return [iso(m), iso(now)] }
  return null
}
function fmtD(d) {
  if (!d) return '—'
  const x = new Date(d); if (isNaN(x)) return d
  return `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`
}
function buildDesc(locs) {
  return (locs || []).map(b => [b.object, b.floor, b.flat, b.room].filter(Boolean).join('/')
    + (b.text ? ': ' + b.text : '')).filter(s => s.trim()).join('\n')
}

// ══════════════════════════════════════════════════════════════════
export default function DennikView() {
  const { user } = useAuth()
  const toast = useToast()
  const canCreate = allowedTypes(user?.role).length > 0

  const [projects, setProjects] = useState([])
  const [entries, setEntries]   = useState([])
  const [vyzvy, setVyzvy]       = useState([])
  const [loading, setLoading]   = useState(true)

  const [selProj, setSelProj]   = useState(ALL)       // id | ALL | null — default Všetky zápisy
  const [selEntry, setSelEntry] = useState(null)      // entry id
  const [typeFilter, setTypeFilter] = useState('all')
  const [rozsahFilter, setRozsahFilter] = useState('all')   // všetky | k stavbe | k bytom
  const [authorFilter, setAuthorFilter] = useState('')
  const [threadSearch, setThreadSearch] = useState('')
  const [dateFilter, setDateFilter] = useState('all')

  const [modal, setModal]   = useState(null)          // null | {record}|{record:null,prefill}
  const [printId, setPrintId] = useState(null)        // projectId pre PDF knihu
  const [genOpen, setGenOpen] = useState(false)       // okno „Vygenerovať výber" denníka (Mirko 22.7.)
  const [genF, setGenF] = useState({ od: '', do: '', osoba: '', byt: '' })
  const [genGo, setGenGo] = useState(null)            // potvrdené filtre → DennikKniha
  const [protokolOpen, setProtokolOpen] = useState(false)  // odovzdávací protokol na PC (Mirko 22.7. „rozšír mview na desktop")
  const [editProtokol, setEditProtokol] = useState(null)   // { id, project_id } — úprava protokolu office-om (Mirko 3.8.)
  const [reloadKey, setReloadKey] = useState(0)       // vynúti refetch detailu po uložení
  const [kat, setKat] = useState(null)                // DENNIK_TUK_V1: katalóg denníka (SK) — práce, prestoje, kto zavinil
  useEffect(() => { fetchAuth('/api/diary/katalog?lang=sk').then(r => r.ok ? r.json() : null).then(setKat).catch(() => {}) }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [pr, en, pp] = await Promise.all([
        fetchAuth('/api/projects').then(r => r.json()),
        fetchAuth('/api/diary/all?limit=500').then(r => r.json()),
        fetchAuth('/api/poter-protokoly').then(r => r.ok ? r.json() : []).catch(() => []),
      ])
      setProjects(Array.isArray(pr) ? pr : [])
      setEntries([
        ...(Array.isArray(en) ? en : []),
        ...(Array.isArray(pp) ? pp.map(protokolToRow) : []),
      ])
      if (isControlRole(user?.role)) {
        const v = await fetchAuth('/api/diary/koniec-etapy/pending').then(r => r.json()).catch(() => [])
        setVyzvy(Array.isArray(v) ? v : [])
      }
    } catch (_) { toast.error('Dáta sa nenačítali.') }
    finally { setLoading(false) }
  }, [toast, user])
  useEffect(() => { load() }, [load])
  useLiveRefresh(load)  // živé obnovenie (SSE, ústava 97)

  useShortcuts([
    { keys: 'alt+n', label: 'Nový zápis', scope: 'diary',
      handler: () => { if (canCreate) setModal({ record: null }) } },
    { keys: '/', label: 'Hľadať', scope: 'diary', handler: () => {
      const el = document.querySelector('[data-diary-search] input'); if (el) el.focus()
    } },
  ])

  // entries → group by project
  const byProject = useMemo(() => {
    const m = {}
    for (const e of entries) { (m[e.project_id] = m[e.project_id] || []).push(e) }
    return m
  }, [entries])
  const vyzvaSet = useMemo(() => new Set(vyzvy.map(v => v.project_id)), [vyzvy])

  // zákazky s počtom zápisov (zoradené — najviac zápisov hore)
  const projRows = useMemo(() => {
    return projects
      .map(p => ({ ...p, _count: (byProject[p.id] || []).length }))
      .filter(p => p._count > 0 || p.status === 'active')
      .sort((a, b) => (b._count - a._count) || String(a.name || '').localeCompare(String(b.name || '')))
  }, [projects, byProject])

  // stredný zoznam — vlákno vybranej zákazky (alebo všetky)
  const thread = useMemo(() => {
    let list = selProj === ALL ? entries : (selProj ? (byProject[selProj] || []) : [])
    if (typeFilter === 'prestoj') list = list.filter(e => !e._isProtokol && Number(e.stoppage_hours) > 0)   // DENNIK_TUK_V1
    else if (typeFilter !== 'all') list = list.filter(e => normType(e.entry_type) === typeFilter)
    // rozsah zápisu (28. 9.): poterový protokol nie je zápis k bytu, pri filtri „k bytom" sa skryje
    if (rozsahFilter === 'byt') list = list.filter(e => !e._isProtokol && e.rozsah === 'byt')
    else if (rozsahFilter === 'stavba') list = list.filter(e => e._isProtokol || e.rozsah !== 'byt')
    if (authorFilter) list = list.filter(e => (e.created_by || '') === authorFilter)
    const dr = dateRange(dateFilter)
    if (dr) list = list.filter(e => String(e.date) >= dr[0] && String(e.date) <= dr[1])
    const q = threadSearch.trim().toLowerCase()
    if (q) list = list.filter(e => hladaj(((e.work_description || '') + ' ' + (e.project_name || '')), q))
    return list.slice().sort((a, b) => String(b.date).localeCompare(String(a.date))
      || String(b.created_at || '').localeCompare(String(a.created_at || '')))
  }, [selProj, entries, byProject, typeFilter, rozsahFilter, authorFilter, threadSearch, dateFilter])

  const authors = useMemo(() => {
    const base = selProj === ALL ? entries : (byProject[selProj] || [])
    return [...new Set(base.map(e => e.created_by).filter(Boolean))].sort()
  }, [selProj, entries, byProject])

  const kpi = useMemo(() => ({
    zak: projRows.length, zap: entries.length, vyz: vyzvy.length,
  }), [projRows, entries, vyzvy])

  // ── klávesnica — ↑↓ navigácia vo vlákne (ústava 56.5) ────────────
  // Klávesová navigácia zoznamu — zdieľaný hook (ústava 54.2, 56.5)
  useListKeyboard({
    items: thread,
    selectedId: selEntry,
    onSelect: setSelEntry,
    onOpen: (row) => { if (row._isProtokol) openProtokolPdf(row); else setModal({ record: row }) },
    enabled: !modal,
  })

  // vybraný zápis vždy v zornom poli (pri ↑↓ skroluje zoznam)
  useEffect(() => {
    if (!selEntry) return
    const el = document.querySelector(`[data-row-id="${selEntry}"]`)
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [selEntry])

  // auto-výber prvého zápisu po zvolení zákazky — detail (a oranžová hlavička) hneď viditeľný
  useEffect(() => {
    if (selProj && !selEntry && thread.length) setSelEntry(thread[0].id)
  }, [selProj, thread, selEntry])

  // možnosti rozbaľovača zákaziek (návrh A — zákazka = dropdown)
  const projOptions = useMemo(() => {
    const o = [{ value: ALL, label: `Všetky zápisy (${entries.length})` }]
    for (const p of projRows) {
      o.push({ value: p.id,
        label: `${p.name || p.id} (${p._count})${vyzvaSet.has(p.id) ? ' · výzva' : ''}` })
    }
    return o
  }, [projRows, entries.length, vyzvaSet])

  function openProject(id) {
    setSelProj(id || null); setSelEntry(null); setTypeFilter('all'); setRozsahFilter('all'); setAuthorFilter('')
  }

  const ROZSAH_FILTERS = [
    { key: 'all', label: 'Všetky', color: COLORS.muted },
    { key: 'stavba', label: 'K stavbe', color: COLORS.nav },
    { key: 'byt', label: 'K bytom', color: COLORS.total },
  ]

  // počty vo filtroch (61.10) — z vlákna vybranej zákazky pred filtrom typu
  const pocty = useMemo(() => {
    const base = selProj === ALL ? entries : (selProj ? (byProject[selProj] || []) : [])
    const c = { all: base.length, prestoj: base.filter(e => !e._isProtokol && Number(e.stoppage_hours) > 0).length }
    base.forEach(e => { const k = normType(e.entry_type); c[k] = (c[k] || 0) + 1 })
    return c
  }, [selProj, entries, byProject])
  const sP = (l, k) => l + ' · ' + (pocty[k] || 0)
  const TYPE_FILTERS = [
    { key: 'all', label: sP('Všetky', 'all'), color: COLORS.muted },
    { key: 'denny', label: sP('Denný', 'denny'), color: COLORS.nav },
    { key: 'prvy', label: sP('Prvý', 'prvy'), color: COLORS.info },
    { key: 'kontrolny', label: sP('Kontrolný', 'kontrolny'), color: COLORS.warn },
    { key: 'posledny', label: sP('Posledný', 'posledny'), color: COLORS.muted },
    { key: 'prestoj', label: sP('S prestojom', 'prestoj'), color: COLORS.warn, hint: 'Zápisy s prestojom — dôvod, kto zavinil, fotka' },
  ]

  // Denník PDF vyžaduje auth v hlavičke (token v query neprejde) → fetch s hlavičkou → blob → nové okno. Mirko 24.7.
  const openDiaryPdf = async (projectId, f = {}) => {
    const w = window.open('', '_blank')
    try {
      const q = new URLSearchParams()
      if (f && f.od) q.set('od', f.od)
      if (f && f.do) q.set('do', f.do)
      if (f && f.osoba) q.set('osoba', f.osoba)
      if (f && f.byt) q.set('byt', f.byt)
      const r = await fetchAuth('/api/diary/pdf/' + encodeURIComponent(projectId) + (q.toString() ? '?' + q.toString() : ''))
      if (!r.ok) { if (w) w.close(); const d = await r.json().catch(() => ({})); throw new Error(d.error || 'HTTP ' + r.status) }
      const url = URL.createObjectURL(await r.blob())
      if (w) { w.location = url } else { const a = document.createElement('a'); a.href = url; a.download = 'dennik.pdf'; a.click(); toast.info('PDF stiahnuté (povoľ pop-up okná pre náhľad).') }
      setTimeout(() => URL.revokeObjectURL(url), 120000)
    } catch (e) { if (w) { try { w.close() } catch (_) {} } toast.error('PDF: ' + e.message) }
  }
  // Poterový protokol v zozname (Mirko 3.8.) — klik otvorí PDF, nie ZapisModal (nemá diary_entries riadok)
  const openProtokolPdf = async (e) => {
    if (!e._pdfFile) { toast.error('PDF k tomuto protokolu zatiaľ nie je vygenerované.'); return }
    const w = window.open('', '_blank')
    try {
      const r = await fetchAuth('/uploads/' + e._pdfFile)
      if (!r.ok) throw new Error('HTTP ' + r.status)
      const url = URL.createObjectURL(await r.blob())
      if (w) { w.location = url } else { toast.info('Povoľ pop-up okná pre náhľad PDF.') }
      setTimeout(() => URL.revokeObjectURL(url), 120000)
    } catch (err) { if (w) { try { w.close() } catch (_) {} } toast.error('PDF: ' + err.message) }
  }
  // Aktívna zákazka pre tlačidlá = zákazka zápisu klikutého v ľavom stĺpci; inak všetky. Mirko 24.7.
  const selEntryObj = useMemo(() => entries.find(x => x.id === selEntry), [entries, selEntry])
  const activeProj = useMemo(() => selEntryObj ? selEntryObj.project_id : ALL, [selEntryObj])
  // Zmazanie zápisu z ⋮ v ľavom stĺpci (Mirko 24.7.).
  const delEntry = async (e) => {
    if (!window.confirm('Zmazať zápis z ' + fmtD(e.date) + '? Nedá sa vrátiť.')) return
    try {
      const r = await fetchAuth('/api/diary/' + e.id, { method: 'DELETE' })
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'HTTP ' + r.status) }
      toast.success('Zápis zmazaný.')
      if (selEntry === e.id) setSelEntry(null)
      load(); setReloadKey(k => k + 1)
    } catch (err) { toast.error('Nepodarilo sa: ' + err.message) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <PageHeader title="Denník" subtitle={`${kpi.zak} zákaziek · ${kpi.zap} zápisov` + (kpi.vyz ? ` · výzvy koniec etapy: ${kpi.vyz}` : '')}>
      <HelpHint text="**ČO:** vlákno zápisov zo stavieb, jeden riadok je jeden zápis; zápisy vznikajú na mobile (parťák **Denný**, majster **Prvý / Kontrolný / Posledný**), tento desktop je dohľad, hľadanie a tlač.

**PREČO:** Matrix vedie **poddodávateľský dôkazný denník** — KAMIR je poddodávateľ, nie zhotoviteľ, a zápis je obrana pri reklamácii aj podklad pre súpis a fakturáciu. Preto sa dá upraviť alebo zmazať **len v deň zápisu**; ďalším dňom sa zamkne (Zákon 50/1976, zákaz antidatovania), riadok dostane značku **zamknuté** a Upraviť aj Zmazať z ponuky ⋮ zmiznú — a kým sa mazať dá, je to zmazanie **natrvalo**, nie archivácia.

**KAM:** zápis patrí k zákazke. Zaškrtnuté **Končím etapu** nie je „koľko som dnes urobil“ — je to žiadosť parťáka o spočítanie podkladov a o výplatu: majster ju overí Kontrolným zápisom, Office z nej robí súpis a mzdu.

**NEPATRÍ sem:** m², výmery ani ceny — tie robí OMERAJ a Výmery, denník má iba popis práce.

**Pozor na čísla a tlačidlá:** zoznam ukazuje **len posledné 3 mesiace** a nikde to nepíše, ale **Náhľad PDF aj Tlač berú celý denník zákazky** a filtre vľavo na PDF nemajú vplyv (vlastný rozsah má len **Výber deň/týždeň/osoba/byt**), takže v PDF býva viac zápisov než na obrazovke. Počítadlo **Zápisy** sčítava aj poterové protokoly, ktoré zápisom denníka nie sú, a počítadlo **Zákazky** ukazuje aj zákazky bez jediného zápisu. Tlačidlá vpravo platia pre zákazku zápisu, na ktorý si klikol — bez kliku robia denník **všetkých** zákaziek; Náhľad PDF a Tlač urobia to isté (otvoria PDF, netlačia) a **Odoslať e-mailom** sa objaví až po kliku na zápis a pošle denník hneď po zadaní adresy, bez náhľadu." />   {/* hinty_v1 */}
        {canCreate && (
          <BtnAdd onClick={() => setModal({ record: null,
            prefill: (activeProj && activeProj !== ALL) ? { project_id: activeProj } : undefined })}>
            + Nový zápis
          </BtnAdd>
        )}
      </PageHeader>

      {/* DENNIK_TUK_V1: KPI karty preč — počty sú v hlavičke a vo filtroch (61.10, priestor na výšku) */}

      {/* Výber zákazky = KLIK na zápis v ľavom stĺpci (Mirko 24.7. — pole „Všetky zápisy" zrušené). Tlačidlá platia pre zákazku klikutého zápisu. */}
      <div style={{ display: 'flex', gap: SPACE.md, alignItems: 'center', marginBottom: SPACE.md }}>
        <div style={{ fontSize: 13, color: COLORS.muted }}>
          {activeProj !== ALL
            ? <>Denník: <b style={{ color: COLORS.text }}>{projects.find(p => p.id === activeProj)?.name || activeProj}</b></>
            : 'Klikni zápis v ľavom stĺpci — tlačidlá platia pre jeho zákazku.'}
        </div>
        <div style={{ flex: 1 }} />
        {(() => {
          const cp = projects.find(p => p.id === activeProj) || null
          const isPoter = !!(cp && (['poter', 'potery'].includes(String(cp.type || '').toLowerCase()) || /potery/.test(String(cp.remesla || ''))))   // SUPIS_JEDNOTNE_V1: aj omietky + potery
          const jednaZakazka = activeProj !== ALL
          const sendDennik = async () => {
            const to = window.prompt('Komu poslať denník e-mailom?')
            if (!to || !to.includes('@')) return
            try {
              const r = await fetchAuth('/api/diary/send-pdf/' + encodeURIComponent(activeProj), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to }) })
              const d = await r.json().catch(() => ({}))
              if (!r.ok) throw new Error(d.error || 'HTTP ' + r.status)
              toast.success('Denník odoslaný na ' + to)
            } catch (e) { toast.error('Neodoslané: ' + e.message) }
          }
          // Mirko 24.7.: ŽIADNE ⋮ — reálne tlačidlá ako v CP editore.
          return (<div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Btn variant="nav" onClick={() => openDiaryPdf(activeProj)}>Náhľad PDF</Btn>
            <Btn variant="ghost" onClick={() => { setGenF({ od: '', do: '', osoba: '', byt: '' }); setGenOpen(true) }}>Výber (deň/týždeň/osoba/byt)</Btn>
            {jednaZakazka && <Btn variant="ghost" onClick={sendDennik}>Odoslať e-mailom</Btn>}
            <Btn variant="ghost" onClick={() => openDiaryPdf(activeProj)}>Tlač</Btn>
            {jednaZakazka && isPoter && <Btn variant="ghost" onClick={() => setProtokolOpen(true)}>Odovzdávací protokol</Btn>}
          </div>)
        })()}
        {loading && <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>Načítavam…</span>}
      </div>

      {/* MASTERDETAIL_WIDTH_FIX_2026-05-31 (Mirko): odstránený override leftWidth={420}, default 300 per ústava sek. 56 */}
      <MasterDetail left={
        selProj == null
          ? <div style={{ ...pad, textAlign: 'center' }}>
              Vyber zákazku hore — zobrazí sa jej vlákno zápisov.
            </div>
          : <ThreadPane
              thread={thread} selProj={selProj} vyzvy={vyzvy}
              title={selProj === ALL ? 'Všetky zápisy'
                : (projects.find(p => p.id === selProj)?.name || selProj)}
              typeFilters={TYPE_FILTERS} typeFilter={typeFilter} setTypeFilter={setTypeFilter}
              rozsahFilters={ROZSAH_FILTERS} rozsahFilter={rozsahFilter} setRozsahFilter={setRozsahFilter}
              authors={authors} authorFilter={authorFilter} setAuthorFilter={setAuthorFilter}
              search={threadSearch} setSearch={setThreadSearch}
              dateFilter={dateFilter} setDateFilter={setDateFilter}
              selEntry={selEntry} setSelEntry={setSelEntry}
              onEdit={(rec) => setModal({ record: rec })}
              onDelete={delEntry}
              canCreate={canCreate}
              onVyzva={(pid) => setModal({ record: null,
                prefill: { project_id: pid, entry_type: 'kontrolny' } })}
              onOpenProtokol={openProtokolPdf}
              onEditProtokol={(e) => setEditProtokol({ id: e._protokolId, project_id: e.project_id })}
              kat={kat}
            />
      } right={
        selEntry
          ? (selEntryObj && selEntryObj._isProtokol
              // Pravý panel protokolu = PRIAMO formulár (ako v Obhliadkach je vpravo editor, nie výpis) — Mirko 3.8.
              ? <div style={{ height: '100%', overflowY: 'auto' }}>
                  <PoterProtokolView key={selEntryObj._protokolId} embedded
                    protokolId={selEntryObj._protokolId}
                    fixedProject={projects.find(p => p.id === selEntryObj.project_id) || null}
                    onSaved={() => load()} />
                </div>
              : <EntryDetail key={selEntry + '_' + reloadKey} entryId={selEntry} kat={kat}
                  onEdit={(rec) => setModal({ record: rec })} />)
          : <EmptyPane text="Vyber zápis z vlákna" />
      } />

      {modal && (
        <ZapisModal open record={modal.record} prefill={modal.prefill}
          user={user} projects={projects} kat={kat}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); setReloadKey(k => k + 1); toast.success('Zápis uložený.') }} />
      )}
      {/* Denník PDF sa generuje cez ústavný letterhead (/api/diary/pdf) — stará A4Template cesta zrušená 24.7. */}
      {genOpen && (() => {
        const gi = { width: '100%', padding: '7px 9px', marginTop: 3, border: `1px solid ${COLORS.line}`, borderRadius: 8, fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' }
        const setWeek = (offset) => { const n = new Date(); const dow = (n.getDay() + 6) % 7; const mon = new Date(n); mon.setDate(n.getDate() - dow + offset * 7); const sun = new Date(mon); sun.setDate(mon.getDate() + 6); setGenF(f => ({ ...f, od: mon.toISOString().slice(0, 10), do: sun.toISOString().slice(0, 10) })) }
        return (
        <Modal open onClose={() => setGenOpen(false)} title="Vygenerovať denník — výber" maxWidth={460}
          footer={<>
            <BtnGhost onClick={() => setGenOpen(false)}>Zrušiť</BtnGhost>
            <BtnAdd onClick={() => { openDiaryPdf(activeProj, genF); setGenOpen(false) }}>Vygenerovať PDF</BtnAdd>
          </>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12, color: COLORS.muted }}>{activeProj !== ALL ? <>Stavba: <b style={{ color: COLORS.text }}>{(projects.find(p => p.id === activeProj) || {}).name || activeProj}</b></> : 'Všetky zákazky (klik zápis vľavo pre konkrétnu)'}</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.muted, marginBottom: 5 }}>Rozsah (deň / týždeň)</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 7, flexWrap: 'wrap' }}>
                <BtnGhost onClick={() => { const t = todayLocal(); setGenF(f => ({ ...f, od: t, do: t })) }}>Dnes</BtnGhost>
                <BtnGhost onClick={() => setWeek(0)}>Tento týždeň</BtnGhost>
                <BtnGhost onClick={() => setWeek(-1)}>Minulý týždeň</BtnGhost>
                <BtnGhost onClick={() => setGenF(f => ({ ...f, od: '', do: '' }))}>Celý denník</BtnGhost>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <label style={{ flex: 1, fontSize: 11, color: COLORS.muted }}>Od<input type="date" value={genF.od} onChange={e => setGenF(f => ({ ...f, od: e.target.value }))} style={gi} /></label>
                <label style={{ flex: 1, fontSize: 11, color: COLORS.muted }}>Do<input type="date" value={genF.do} onChange={e => setGenF(f => ({ ...f, do: e.target.value }))} style={gi} /></label>
              </div>
              <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 3 }}>Prázdne = celý denník zákazky.</div>
            </div>
            <label style={{ fontSize: 11, color: COLORS.muted }}>Osoba (nepovinné) — meno robotníka alebo autora zápisu<input value={genF.osoba} onChange={e => setGenF(f => ({ ...f, osoba: e.target.value }))} placeholder="napr. Novák" style={gi} /></label>
            <label style={{ fontSize: 11, color: COLORS.muted }}>Byt / objekt (nepovinné)<input value={genF.byt} onChange={e => setGenF(f => ({ ...f, byt: e.target.value }))} placeholder="napr. B12" style={gi} /></label>
          </div>
        </Modal>
        )
      })()}
      {protokolOpen && (() => { const cp = projects.find(p => p.id === activeProj); return cp ? (
        <Modal open onClose={() => setProtokolOpen(false)} title={'Odovzdávací protokol — ' + (cp.name || cp.id)} maxWidth={1100}>
          <PoterProtokolView embedded fixedProject={cp} onSaved={() => setProtokolOpen(false)} />
        </Modal>
      ) : null })()}
      {/* ÚPRAVA existujúceho protokolu — office ho musí vedieť dopísať, keď na stavbe vypadol signál (Mirko 3.8.) */}
      {editProtokol && (() => { const cp = projects.find(p => p.id === editProtokol.project_id); return (
        <Modal open onClose={() => setEditProtokol(null)} title={'Upraviť poterový protokol ' + editProtokol.id} maxWidth={1100}>
          <PoterProtokolView embedded protokolId={editProtokol.id} fixedProject={cp || null}
            onSaved={() => { setEditProtokol(null); load() }} />
        </Modal>
      ) })()}
    </div>
  )
}

const pad = { padding: SPACE.lg, color: COLORS.muted, fontSize: TYPO.sizes.sm }

// ══════════════════════════════════════════════════════════════════
// THREAD PANE — stredný panel: vlákno zápisov + filtre + výzva + akcie
// ══════════════════════════════════════════════════════════════════
function ThreadPane({ thread, selProj, title, vyzvy, typeFilters, typeFilter, setTypeFilter,
  rozsahFilters, rozsahFilter, setRozsahFilter,
  authors, authorFilter, setAuthorFilter, search, setSearch, dateFilter, setDateFilter, selEntry, setSelEntry,
  onEdit, onDelete, onVyzva, canCreate, onOpenProtokol, onEditProtokol, kat }) {
  const { meno } = usePersony()
  const fotkaPovinna = useMemo(() => new Set((kat?.prestoje || []).filter(p => p.fotka_povinna).map(p => p.kluc)), [kat])
  const isAll = selProj === ALL
  const _selE = (thread || []).find(e => e.id === selEntry)
  const _vp = _selE ? _selE.project_id : null
  const vyzva = _vp ? vyzvy.find(v => v.project_id === _vp) : null

  return (
    <>
      {/* HLAVIČKA KARTY — tenký pásik + čierny názov (ústava 61.6) */}
      <div style={{ height: 5, background: COLORS.nav }} />
      <div style={{ padding: '8px 12px', fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.sm,
        color: COLORS.text, borderBottom: `1px solid ${COLORS.line}`,
        whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {title}
      </div>

      {/* VÝZVA — parťák ohlásil koniec etapy (ústava 4.A) */}
      {vyzva && (
        <div style={{ margin: SPACE.sm, padding: SPACE.sm, borderRadius: RADIUS.md,
          border: `2px solid ${COLORS.total}`, background: COLORS.totalBg }}>
          <div style={{ fontWeight: TYPO.weights.bold, color: COLORS.total, fontSize: TYPO.sizes.sm }}>
            Parťák ohlásil koniec etapy
          </div>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.total, margin: '2px 0 6px' }}>
            Zápis {fmtD(vyzva.date)} · over Kontrolným / Posledným zápisom.
          </div>
          {canCreate && (
            <Btn variant="total" onClick={() => onVyzva(_vp)}>Over kontrolným zápisom →</Btn>
          )}
        </div>
      )}

      {/* filtre */}
      <FilterChips options={typeFilters} value={typeFilter} onChange={setTypeFilter} />
      {/* rozsah zápisu — stavebný denník vs. zápisy k jednotlivým bytom */}
      <FilterChips options={rozsahFilters} value={rozsahFilter} onChange={setRozsahFilter} />
      <div style={{ padding: SPACE.sm, display: 'flex', flexDirection: 'column', gap: 6,
        borderBottom: `1px solid ${COLORS.line}` }}>
        <SearchableSelect value={authorFilter} onChange={setAuthorFilter}
          emptyLabel="— ktokoľvek zapísal —"
          options={authors.map(a => ({ value: a, label: a }))}
          placeholder="Filter: kto zapísal…" />
        <input type="search" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Hľadať v popise…" style={inp} />
      </div>
      {/* preset dátumové filtre — rýchly výpis zápisov */}
      <FilterChips options={DATE_FILTERS} value={dateFilter} onChange={setDateFilter} />

      {/* vlákno */}
      <div style={{ flex: 1, overflow: 'auto', padding: 6 }}>
        {thread.length === 0 ? (
          <div style={pad}>Žiadny zápis.</div>
        ) : thread.map(e => {
          const nt = normType(e.entry_type)
          const ti = TYPES[nt] || TYPES.denny
          if (e._isProtokol) {
            return (
              <ListRow key={e.id} dataId={e.id} selected={selEntry === e.id} stripColor={ti.color}
                onClick={() => setSelEntry(e.id)} onDoubleClick={() => onOpenProtokol(e)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: TYPO.weights.semibold, color: COLORS.text }}>{fmtD(e.date)}</span>
                      <TypeTag t={nt} />
                    </div>
                    <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono, marginTop: 2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {isAll ? (e.project_name || e.project_id) + ' · ' : ''}{e.work_description} · {meno(e.created_by) || '—'}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    <RowMenu actions={[
                      { label: 'Otvoriť PDF', onClick: () => onOpenProtokol(e) },
                      ...(onEditProtokol ? [{ label: 'Upraviť protokol', onClick: () => onEditProtokol(e) }] : []),
                    ]} />
                  </div>
                </div>
              </ListRow>
            )
          }
          return (
            <ListRow key={e.id} dataId={e.id} selected={selEntry === e.id} stripColor={ti.color}
              onClick={() => setSelEntry(e.id)} onDoubleClick={() => onEdit(e)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: TYPO.weights.semibold, color: COLORS.text }}>{fmtD(e.date)}</span>
                    <TypeTag t={nt} />
                    {e.rozsah === 'byt' && e.byt_cislo && <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.total, background: COLORS.totalBg, borderRadius: RADIUS.sm, padding: '1px 6px' }}>{bytNazov(e.byt_cislo, 'BYT')}</span>}
                    {e.created_via === 'anton' && <span title="Text vložil asistent Anton na príkaz autora" style={{ fontSize: 10, fontWeight: 700, color: COLORS.muted, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, padding: '0 5px' }}>ANTON</span>}
                    {!!e.koniec_etapy && <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.total, background: COLORS.totalBg, borderRadius: RADIUS.sm, padding: '1px 6px' }}>KONIEC ETAPY</span>}
                    {e.is_locked && <span title="Zamknuté — uplynul deň zápisu (Zákon 50/1976)" style={{ fontSize: 10, color: COLORS.muted, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, padding: '0 5px' }}>zamknuté</span>}
                    {Number(e.stoppage_hours) > 0 && <span title={(e.stoppage_reason || '') + (e.stoppage_note ? ' (' + e.stoppage_note + ')' : '')} style={{ fontSize: 10, fontWeight: 700, color: COLORS.warn, background: '#FEF3C7', borderRadius: RADIUS.sm, padding: '1px 6px' }}>PRESTOJ {e.stoppage_hours} h</span>}
                    {Number(e.stoppage_hours) > 0 && fotkaPovinna.has(e.stoppage_kluc) && !Number(e.prestoj_foto_count) && <span title="Dôvod prestoja žiada fotku — bez nej sa prestoj ťažko fakturuje" style={{ fontSize: 10, fontWeight: 700, color: COLORS.remove, background: COLORS.removeBg, borderRadius: RADIUS.sm, padding: '1px 6px' }}>CHÝBA FOTKA</span>}
                  </div>
                  <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, fontFamily: TYPO.mono, marginTop: 2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {isAll ? (e.project_name || e.project_id) + ' · ' : ''}{(e.work_description || '—').split('\n')[0]} · {meno(e.created_by) || '—'}
                  </div>
                </div>
                <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {e.photo_count > 0 && <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.xs }}>{e.photo_count} foto</span>}
                  <RowMenu actions={[
                    { label: 'Zobraziť detail', onClick: () => setSelEntry(e.id) },
                    ...(e.is_locked ? [] : [{ label: 'Upraviť', onClick: () => onEdit(e) }]),
                    ...((canCreate && !e.is_locked && onDelete) ? [{ separator: true }, { label: 'Zmazať', onClick: () => onDelete(e), danger: true }] : []),
                  ]} />
                </div>
              </div>
            </ListRow>
          )
        })}
      </div>
      <ListStatusBar>{thread.length} zápisov · 2× klik = upraviť</ListStatusBar>
    </>
  )
}

function TypeTag({ t }) {
  const ti = TYPES[t] || TYPES.denny
  return (
    <span style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold,
      color: ti.color, background: TINT.nav, borderRadius: RADIUS.sm, padding: '1px 7px' }}>
      {ti.label}
    </span>
  )
}

// ══════════════════════════════════════════════════════════════════
// PROTOKOL DETAIL — pravý panel pre poterový protokol (nemá diary_entries riadok, plné dáta z /api/poter-protokoly/:id)
// ══════════════════════════════════════════════════════════════════
function ProtokolDetailPane({ e, onOpen, onEdit }) {
  const { meno } = usePersony()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true); setData(null)
    fetchAuth('/api/poter-protokoly/' + e._protokolId).then(r => r.json())
      .then(d => setData(d)).catch(() => setData(null)).finally(() => setLoading(false))
  }, [e._protokolId])

  if (loading) return <div style={{ padding: SPACE.lg, color: COLORS.muted }}>Načítavam…</div>
  if (!data) return <div style={{ padding: SPACE.lg, color: COLORS.remove }}>Protokol sa nepodarilo načítať.</div>

  let checklist = []
  try { checklist = JSON.parse(data.checklist_json || '[]') } catch (_) {}
  const podlazia = (data.podlazia || []).map(p => {
    let foto = []
    try { foto = JSON.parse(p.foto_json || '[]') } catch (_) {}
    return { ...p, foto }
  })

  return (
    <div>
      <div style={{ height: 5, background: COLORS.add }} />
      <div style={{ padding: SPACE.lg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: SPACE.sm, marginBottom: SPACE.md }}>
          <div>
            <div style={{ fontWeight: TYPO.weights.bold, fontSize: TYPO.sizes.lg }}>Poterový protokol {data.id}</div>
            <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, marginTop: 2 }}>
              {fmtD((data.created_at || '').slice(0, 10))} · zapísal {meno(data.created_by) || '—'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {onEdit && <Btn variant="nav" onClick={onEdit}>Upraviť</Btn>}
            <BtnAdd onClick={() => onOpen(e)}>Otvoriť PDF</BtnAdd>
          </div>
        </div>

        <DRow l="Materiál" v={data.material_name ? `${data.material_name} (${data.material_code}) — min. ${data.min_hrubka_ref_mm} mm` : '—'} />
        <DRow l="Objednávateľ" v={data.objednavatel_typ === 'firma' ? 'Firma' : 'Rodinný dom'} />
        <DRow l="Celkovo" v={`${data.celkovo_m3 || 0} m³ · ${data.celkovo_m2 || 0} m²`} />

        <div style={{ padding: '6px 0', borderBottom: `1px solid ${COLORS.line}` }}>
          <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4, fontWeight: TYPO.weights.semibold, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Kontrolný list — poruchy/nezhody
          </div>
          {checklist.length === 0 ? <div style={{ color: COLORS.muted }}>—</div> : checklist.map(c => (
            <div key={c.key} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: TYPO.sizes.sm, padding: '2px 0', color: c.checked ? COLORS.text : COLORS.remove }}>
              <span>{c.checked ? '✓' : '✕'}</span><span>{c.label}</span>
            </div>
          ))}
        </div>

        {podlazia.map((p, i) => (
          <div key={i} style={{ padding: '8px 0', borderBottom: `1px solid ${COLORS.line}` }}>
            <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4, fontWeight: TYPO.weights.semibold, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Podlažie {p.podlazie || '—'}
            </div>
            <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.text }}>
              dohodnutá {p.dohodnuta_vyska_cm || 0} cm · min. hrúbka {p.min_hrubka_mm || 0} mm · vyčerpané {p.vycerpane_m3 || 0} m³ · plocha {p.plocha_m2 || 0} m²
            </div>
            {p.popis && <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, marginTop: 2 }}>{p.popis}</div>}
            {p.foto.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {p.foto.map((u, k) => (
                  <a key={k} href={u} target="_blank" rel="noreferrer">
                    <img src={u} style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: RADIUS.md, border: `1px solid ${COLORS.line}` }} />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}

        <DRow l="Dodací list" v={data.dodaci_list} />
        <DRow l="Počasie / teplota" v={[data.pocasie, data.teplota].filter(Boolean).join(' · ')} />
        <DRow l="Podpísal zákazník" v={data.signed_name} />
        <DRow l="Za KAMIR" v={data.kamir_signed_name} />
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// ENTRY DETAIL — pravý panel: detail zápisu + fotky + audit
// ══════════════════════════════════════════════════════════════════
function EntryDetail({ entryId, onEdit, kat }) {
  const toast = useToast()
  const { user } = useAuth()
  const { meno } = usePersony()
  const [data, setData] = useState(null)
  const [tab, setTab] = useState('detail')
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  // PHOTO_CAPTION_2026-05-29 (Mirko): klik na fotku → modal s popisom
  const [photoModal, setPhotoModal] = useState(null) // { id, project_id, filename, thumb_filename, caption }
  const [captionDraft, setCaptionDraft] = useState('')
  const [savingCaption, setSavingCaption] = useState(false)

  const reload = useCallback(() => {
    setLoading(true)
    fetchAuth('/api/diary/' + entryId).then(r => r.json())
      .then(d => setData(d)).catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [entryId])
  useEffect(() => { reload() }, [reload])

  // m² z poter zápisu → súpis s POTVRDENÍM (ústava 674 — žiadna tichá automatika; Mirko 20.7.)
  async function zapisDoSupisu() {
    const d = data
    const dt = new Date((d.date || '') + 'T00:00:00')
    if (isNaN(dt.getTime())) { toast.error('Zápis nemá platný dátum.'); return }
    const cena = Number(d.cena_m2) || 0
    const suhrn = 'Zapísať do súpisu ' + (dt.getMonth() + 1) + '/' + dt.getFullYear() + '?\n\n'
      + 'Zákazka: ' + (d.project_name || d.project_id) + '\n'
      + 'Popis: Poter — ' + (d.material_name || 'materiál ?') + (d.hrubka_mm ? ' ' + d.hrubka_mm + ' mm' : '') + '\n'
      + 'Výmera: ' + d.m2_done + ' m² × ' + cena + ' €/m² = ' + (Math.round(d.m2_done * cena * 100) / 100) + ' €'
      + (cena ? '' : '\n\nCena je 0 — doplň potom v súpise.')
    if (!window.confirm(suhrn)) return
    try {
      const r = await fetchAuth('/api/supis-polozky', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: d.project_id, year: dt.getFullYear(), month: dt.getMonth() + 1,
          popis: 'Poter — ' + (d.material_name || '') + (d.hrubka_mm ? ' ' + d.hrubka_mm + ' mm' : '') + ' (denník ' + d.date + ')',
          mj: 'm²', vymera: Number(d.m2_done) || 0, cena_mj: cena, diary_entry_id: d.id,
        }),
      })
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || 'HTTP ' + r.status) }
      toast.success('Zapísané do súpisu')
      reload()
    } catch (e) { toast.error('Nepodarilo sa: ' + e.message) }
  }

  // PHOTO_CAPTION_2026-05-29: PUT /api/diary/photos/:id/caption
  async function saveCaption() {
    if (!photoModal) return
    setSavingCaption(true)
    try {
      const r = await fetchAuth('/api/diary/photos/' + photoModal.id + '/caption', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ caption: captionDraft }),
      })
      if (!r.ok) throw new Error('save failed')
      toast.success('Popis uložený')
      setPhotoModal(null)
      reload()
    } catch (_) { toast.error('Chyba uloženia') }
    finally { setSavingCaption(false) }
  }

  // foto upload k zápisu (desktop — ústava 25.4)
  async function uploadPhoto(ev) {
    const files = Array.from(ev.target.files || [])
    ev.target.value = ''
    if (!files.length || !data?.id) return
    const fd = new FormData()
    files.forEach(f => fd.append('photos', f))
    setUploading(true)
    try {
      const r = await fetchAuth('/api/diary/' + data.project_id + '/photos/' + data.id,
        { method: 'POST', body: fd })
      if (r.ok) { toast.success('Foto nahraté.'); reload() }
      else toast.error('Foto sa nenahralo.')
    } catch (_) { toast.error('Foto sa nenahralo.') }
    finally { setUploading(false) }
  }

  if (loading) return <div style={{ ...detailBox, ...pad }}>Načítavam…</div>
  if (!data) return <div style={{ ...detailBox, ...pad }}>Zápis sa nenačítal.</div>

  const nt = normType(data.entry_type)
  let locs = []
  try { const a = JSON.parse(data.locations || '[]'); if (Array.isArray(a)) locs = a } catch (_) {}
  const photos = Array.isArray(data.photos) ? data.photos : []
  const temp = data.temp_avg != null ? data.temp_avg : data.temp_min

  return (
    <div style={detailBox}>
      {/* HLAVIČKA KARTY — tenký stavový pásik + čierny názov (ústava 61.6) */}
      <div style={{ height: 4, background: COLORS.brand }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md,
        padding: `${SPACE.md}px ${SPACE.lg}px`, borderBottom: `1px solid ${COLORS.line}` }}>
        <h2 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.extra,
          color: COLORS.text }}>
          {fmtD(data.date)}
        </h2>
        <TypeTag t={nt} />
        {!!data.koniec_etapy && <span style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
          color: COLORS.total, background: COLORS.totalBg, borderRadius: RADIUS.sm,
          padding: '1px 7px' }}>KONIEC ETAPY</span>}
        {/* DIARY_24H_LOCK_2026-05-29: lock badge per ústava sek. 39 */}
        {data.is_locked && <span style={{ fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
          color: '#92400e', background: '#FBFAF7', border: '1px solid #15171A',
          borderRadius: RADIUS.sm, padding: '1px 7px' }} title="Zákon 50/1976 — antidatovanie">
          ZAMKNUTÉ
        </span>}
        <div style={{ flex: 1 }} />
        {!data.is_locked && <Btn variant="nav" onClick={() => onEdit(data)}>Upraviť</Btn>}
      </div>
      {/* DIARY_24H_LOCK_2026-05-29: info banner pre zamknuté zápisy */}
      {data.is_locked && (
        <div style={{ background: '#FBFAF7', borderBottom: `1px solid #15171A`,
          padding: `${SPACE.sm}px ${SPACE.lg}px`, fontSize: TYPO.sizes.sm, color: '#92400e' }}>
          <strong>Zápis je zamknutý</strong> — uplynul deň zápisu (začal nový deň).
          Zmena obsahu, pridanie fotiek ani vymazanie nie sú možné (Zákon č. 50/1976 Zb. — zákaz antidatovania).
          Popis fotky možno upraviť (nemení dôkaznú hodnotu).
        </div>
      )}

      <div style={{ padding: SPACE.md, overflow: 'auto', flex: 1, background: COLORS.bg,
        border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg }}>
        <Tabs value={tab} onChange={setTab} tabs={[
          { key: 'detail', label: 'Detail' },
          { key: 'foto', label: 'Foto', count: photos.length },
          { key: 'history', label: 'História' },
        ]} />

        {tab === 'detail' && (
          <div style={{ fontSize: TYPO.sizes.base, lineHeight: 1.7 }}>
            <DRow l="Zákazka" v={data.project_name || data.project_id} />
            <DRow l="Rozsah" v={data.rozsah === 'byt'
              ? 'Zápis k bytu' + (data.byt_cislo ? (bytMaSlovo(data.byt_cislo) ? ' · ' : ' ') + data.byt_cislo : '')
              : 'Zápis k stavbe'} />
            {data.created_via === 'anton' && (
              <DRow l="Vložil" v="asistent Anton na príkaz autora zápisu" />
            )}
            <DRow l="Počasie" v={[WEATHER_LABEL[data.weather_type] || data.weather_type,
              temp != null ? temp + ' °C' : null].filter(Boolean).join(' · ')} />
            <DRow l="Pracovníci" v={data.worker_count} />
            {cudziOriginal(data) ? (
              <>
                <DRow l="Popis prác" v={data.work_description} />
                <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm, padding: '2px 0 4px', whiteSpace: 'pre-wrap' }}>
                  ({cudziOriginal(data).lang} originál: {cudziOriginal(data).text})
                </div>
              </>
            ) : locs.length > 0 ? (
              <div style={{ display: 'flex', gap: SPACE.md, padding: '4px 0' }}>
                <span style={dLbl}>Popis prác</span>
                <span style={{ color: COLORS.text }}>
                  {locs.map((b, i) => (
                    <div key={i}>• {[b.object, b.floor, b.flat, b.room].filter(Boolean).join(' / ')}
                      {b.text ? ' — ' + b.text : ''}{b.done ? '  ✓ hotové' : ''}</div>
                  ))}
                </span>
              </div>
            ) : <DRow l="Popis prác" v={data.work_description} />}
            {(data.material_name || data.m2_done || data.hrubka_mm || data.spotreba || data.cena_m2 || data.poznamka) ? (
              <div style={{ margin: '6px 0', padding: '8px 12px', background: 'rgba(2,132,199,0.06)', borderRadius: 6, borderLeft: '3px solid #0369a1' }}>
                <div style={{ fontWeight: 700, color: '#0369a1', marginBottom: 4 }}>Poterový protokol</div>
                {data.material_name ? <DRow l="Materiál" v={data.material_name} /> : null}
                {data.m2_done ? <DRow l="Plocha" v={data.m2_done + ' m²'} /> : null}
                {data.hrubka_mm ? <DRow l="Hrúbka" v={data.hrubka_mm + ' mm'} /> : null}
                {data.spotreba ? <DRow l="Spotreba materiálu" v={data.spotreba} /> : null}
                {(data.vlhkost_pct != null && data.vlhkost_pct !== '') ? <DRow l="Vlhkosť" v={data.vlhkost_pct + ' %'} /> : null}
                {data.pevnost ? <DRow l="Pevnosť" v={data.pevnost} /> : null}
                {data.cena_m2 ? <DRow l="Cena" v={data.cena_m2 + ' €/m²'} /> : null}
                {data.poznamka ? <DRow l="Poznámka" v={data.poznamka} /> : null}
                {data.supis_ref ? (
                  <div style={{ marginTop: 4, color: '#16a34a', fontWeight: 600, fontSize: TYPO.sizes.sm }}>
                    V súpise: {data.supis_ref.id} · {data.supis_ref.month}/{data.supis_ref.year} · {data.supis_ref.vymera} m²{data.supis_ref.celkom ? ' · ' + data.supis_ref.celkom + ' €' : ''}
                  </div>
                ) : (Number(data.m2_done) > 0 && ['admin', 'office_manager'].includes(user?.role)) ? (
                  <div style={{ marginTop: 6 }}>
                    <BtnGhost onClick={zapisDoSupisu}>Zapísať {data.m2_done} m² do súpisu</BtnGhost>
                  </div>
                ) : null}
              </div>
            ) : null}
            {/* DENNIK_TUK_V1: práce z dlaždíc + prestoj s dôvodom, kto zavinil a fotkou (ústava 25.1) */}
            {jsonPole(data.prace_kluce).length > 0 && (
              <div style={{ padding: '6px 0', borderBottom: `1px solid ${COLORS.line}` }}>
                <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 4, fontWeight: TYPO.weights.semibold, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Vybrané z katalógu</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{jsonPole(data.prace_kluce).map(k => (
                  <span key={k} style={{ fontSize: TYPO.sizes.xs, fontWeight: 600, color: COLORS.nav, background: COLORS.navBg, borderRadius: 999, padding: '2px 9px' }}>{textKatalogu(kat, k)}</span>
                ))}</div>
              </div>
            )}
            {jsonPole(data.na_com).length > 0 && <DRow l="Na čom" v={jsonPole(data.na_com).map(k => textKatalogu({ sady: [{ polozky: kat?.na_com || [] }] }, k)).join(', ')} />}
            {jsonPole(data.materialy).length > 0 && <DRow l="Materiál" v={jsonPole(data.materialy).map(m => m.text + (m.ref ? ' (' + m.ref.replace(/^plu:/, 'PLU ').replace(/^qc:/, '') + ')' : '')).join(', ')} />}
            {!!data.stoppage_hours && (
              <div style={{ margin: '6px 0', padding: '8px 12px', background: '#FFFBEB', borderRadius: 6, borderLeft: `3px solid ${COLORS.warn}` }}>
                <div style={{ fontWeight: 700, color: COLORS.warn, marginBottom: 4 }}>Prestoj</div>
                <DRow l="Koľko" v={Number(data.stoppage_hours) >= 8 ? data.stoppage_hours + ' h (celý deň)' : data.stoppage_hours + ' h'} />
                <DRow l="Dôvod" v={data.stoppage_reason} />
                <DRow l="Kto zavinil" v={zavinilText(kat, data.stoppage_zavinil) || String(data.stoppage_note || '').replace(/^zavinil\s+/, '')} />
                {(() => {
                  const fp = photos.filter(p => p.ucel === 'prestoj')
                  const treba = (kat?.prestoje || []).some(p => p.kluc === data.stoppage_kluc && p.fotka_povinna)
                  if (!fp.length) return treba ? <div style={{ color: COLORS.remove, fontWeight: 700, fontSize: TYPO.sizes.sm, padding: '4px 0' }}>Chýba fotka prestoja — dôvod ju žiada (dôkaz pre fakturáciu)</div> : null
                  return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACE.sm, paddingTop: 6 }}>{fp.map(p => (
                      <button key={p.id} type="button" onClick={() => { setPhotoModal({ ...p, project_id: data.project_id }); setCaptionDraft(p.caption || '') }}
                        style={{ padding: 0, background: 'transparent', border: 'none', cursor: 'pointer' }} title={p.caption || ''}>
                        <img src={'/uploads/diary/' + data.project_id + '/' + (p.thumb_filename || p.filename)} alt={p.caption || ''}
                          style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: RADIUS.md, border: `2px solid ${COLORS.warn}` }} />
                      </button>
                    ))}</div>
                  )
                })()}
              </div>
            )}
            {data.issues ? <DRow l="Opatrenie" v={data.issues} /> : null}
            {data.notes ? <DRow l="Prevzatie/odovzdanie" v={data.notes} /> : null}
            <DRow l="Zapísal" v={meno(data.created_by)} />
            {data.updated_by ? <DRow l="Upravil" v={meno(data.updated_by)} /> : null}
            {photos.length > 0 && (
              <div style={{ display: 'flex', gap: SPACE.md, padding: '8px 0 0' }}>
                <span style={dLbl}>Foto ({photos.length})</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACE.sm }}>
                  {photos.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => { setPhotoModal({ ...p, project_id: data.project_id }); setCaptionDraft(p.caption || '') }}
                      title={p.caption || 'Klikni pre popis'}
                      style={{ padding: 0, background: 'transparent', border: 'none', cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: 80 }}>
                      <img src={'/uploads/diary/' + data.project_id + '/' + (p.thumb_filename || p.filename)}
                        alt={p.caption || ''} style={{ width: 80, height: 80, objectFit: 'cover',
                          borderRadius: RADIUS.md, border: `1px solid ${p.caption ? COLORS.brand : COLORS.line}` }} />
                      <div style={{ fontSize: 10, color: p.caption ? COLORS.text : COLORS.muted,
                        maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        textAlign: 'center', fontStyle: p.caption ? 'normal' : 'italic' }}>
                        {p.caption || '+ popis'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'foto' && (
          <div>
            {/* upload k zápisu — ústava 25.4; DIARY_24H_LOCK_2026-05-29: skryté ak zamknuté */}
            {!data.is_locked ? (
              <label style={uploadBtn}>
                {uploading ? 'Nahrávam…' : '+ Pridať foto'}
                <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                  disabled={uploading} onChange={uploadPhoto} />
              </label>
            ) : (
              <div style={{ padding: SPACE.md, background: '#FBFAF7', border: `1px solid #15171A`,
                borderRadius: RADIUS.md, color: '#92400e', fontSize: TYPO.sizes.sm }}>
                Zápis zamknutý — nové fotky nemožno pridať. Popis existujúcich fotiek možno upraviť kliknutím.
              </div>
            )}
            {photos.length === 0 ? (
              <div style={pad}>Zatiaľ žiadne fotky — pridaj cez tlačidlo vyššie.</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACE.md, marginTop: SPACE.md }}>
                {photos.map(p => {
                  const url = '/uploads/diary/' + data.project_id + '/' + (p.thumb_filename || p.filename)
                  return (
                    <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 140 }}>
                      <button type="button"
                        onClick={() => { setPhotoModal({ ...p, project_id: data.project_id }); setCaptionDraft(p.caption || '') }}
                        title={p.caption || 'Klikni pre popis'}
                        style={{ padding: 0, background: 'transparent', border: 'none', cursor: 'pointer' }}>
                        <img src={url} alt={p.caption || ''} style={{ width: 140, height: 140, objectFit: 'cover',
                          borderRadius: RADIUS.md, border: `1px solid ${p.caption ? COLORS.brand : COLORS.line}` }} />
                      </button>
                      <div style={{ fontSize: TYPO.sizes.xs,
                        color: p.caption ? COLORS.text : COLORS.muted,
                        fontStyle: p.caption ? 'normal' : 'italic',
                        textAlign: 'center', wordBreak: 'break-word' }}>
                        {p.ucel === 'prestoj' ? <b style={{ color: COLORS.warn, fontStyle: 'normal' }}>PRESTOJ · </b> : p.ucel === 'pred_stav' ? <b style={{ color: COLORS.nav, fontStyle: 'normal' }}>PRED ZAČATÍM · </b> : null}
                        {p.caption || '+ popis fotky'}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'history' && <HistoryPanel module="diary" entityId={data.id} />}
      </div>

      {/* PHOTO_CAPTION_2026-05-29 (Mirko): modal na popis fotky — desktop varianta */}
      {photoModal && (
        <div onClick={() => !savingCaption && setPhotoModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACE.lg }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: COLORS.bg, borderRadius: RADIUS.lg, maxWidth: 700, width: '100%',
              maxHeight: '90vh', overflow: 'auto', padding: SPACE.lg,
              display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.bold }}>
                Popis fotky
              </h3>
              <button onClick={() => setPhotoModal(null)} disabled={savingCaption}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 20, color: COLORS.muted }}>✕</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={'/uploads/diary/' + photoModal.project_id + '/' + photoModal.filename}
                alt="" style={{ maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain',
                  borderRadius: RADIUS.md, border: `1px solid ${COLORS.line}` }} />
            </div>
            <textarea value={captionDraft}
              onChange={e => setCaptionDraft(e.target.value)}
              placeholder="Čo je na fotke? Napr.: pred maľovaním 2.NP, prasklina na stene, zakryté konštrukcie pred zamurovaním…"
              rows={3} autoFocus
              style={{ width: '100%', padding: SPACE.sm, fontSize: TYPO.sizes.base,
                border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
                fontFamily: 'inherit', resize: 'vertical', minHeight: 80, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => setPhotoModal(null)} disabled={savingCaption}>
                Zrušiť
              </Btn>
              <Btn variant="add" onClick={saveCaption} disabled={savingCaption}>
                {savingCaption ? 'Ukladám…' : '✓ Uložiť popis'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const detailBox = { background: COLORS.bg, border: `1px solid ${COLORS.line}`,
  borderRadius: RADIUS.lg, height: '100%', display: 'flex', flexDirection: 'column',
  overflow: 'hidden' }
// štítok na oranžovej hlavičke karty — biele pozadie, čierny text (ústava 61.6)
const hdrTag = { fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold,
  color: COLORS.brandText, background: '#fff', borderRadius: RADIUS.sm, padding: '2px 8px' }
// akčné tlačidlo „+ Pridať foto" — modré (ústava 61.1)
const uploadBtn = { display: 'inline-flex', alignItems: 'center', gap: 6,
  background: COLORS.nav, color: '#fff', border: 'none', borderRadius: RADIUS.md,
  padding: '8px 16px', fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.bold,
  cursor: 'pointer', fontFamily: 'inherit' }
const dLbl = { width: 150, color: COLORS.muted, flexShrink: 0 }
function cudziOriginal(x) {
  const l = String(x?.original_lang || '').toLowerCase()
  if (!l || l === 'sk') return null
  const o = String(x?.original_text || '').trim()
  if (!o || o === String(x?.work_description || '').trim()) return null
  return { lang: l.toUpperCase(), text: o }
}

function DRow({ l, v }) {
  // Zjednoteny CP-style detail (Mirko 25.7. „pravy stlpec tymto stylom"): label uppercase hore, hodnota dole.
  return (
    <div style={{ padding: '6px 0', borderBottom: `1px solid ${COLORS.line}` }}>
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginBottom: 2,
        fontWeight: TYPO.weights.semibold, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{l}</div>
      <div style={{ color: COLORS.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{(v === 0 || v) ? v : '—'}</div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════
// ZAPIS MODAL — nový / edit zápis (useRecordEditor: lock + dirty)
// vlastný Form→Confirm krok (12.0/3) — komplexný formulár.
// ══════════════════════════════════════════════════════════════════
function ZapisModal({ open, record, prefill, user, projects, kat, onClose, onSaved }) {
  const isNew = !record
  const toast = useToast()
  const types = allowedTypes(user?.role)
  const [step, setStep] = useState('form')   // form | confirm

  const initLocs = (() => {
    try { const a = JSON.parse(record?.locations || '[]'); if (Array.isArray(a) && a.length) return a } catch (_) {}
    return [{ object: '', floor: '', room: '', text: record?.work_description || '', done: false }]
  })()

  const ed = useRecordEditor({
    resource: 'diary',
    id: record?.id || null,
    user,
    initial: {
      project_id:   record?.project_id || prefill?.project_id || '',
      entry_type:   record ? normType(record.entry_type) : (prefill?.entry_type || types[0] || 'denny'),
      // dva typy zápisov (Mirko 28. 9.): 'stavba' = k celej stavbe / objektu / poschodiu,
      // 'byt' = k jednému konkrétnemu bytu (byt sa zadáva raz za celý zápis)
      rozsah:       record?.rozsah === 'byt' ? 'byt' : 'stavba',
      byt:          record?.byt_cislo || '',
      date:         record?.date || today(),
      weather_type: record?.weather_type || '',
      temp_avg:     record?.temp_avg ?? '',
      worker_count: record?.worker_count ?? 1,
      stoppage_hours:  record?.stoppage_hours ?? '',
      stoppage_reason: record?.stoppage_reason || '',
      stoppage_note:   record?.stoppage_note || '',
      stoppage_kluc:   record?.stoppage_kluc || '',
      stoppage_zavinil: record?.stoppage_zavinil || '',
      prace_kluce:     jsonPole(record?.prace_kluce),
      koniec_etapy: record?.koniec_etapy ? 1 : 0,
      material_name: record?.material_name || '',
      m2_done: record?.m2_done ?? '',
      hrubka_mm: record?.hrubka_mm ?? '',
      spotreba: record?.spotreba ?? '',
      cena_m2: record?.cena_m2 ?? '',
      vlhkost_pct: record?.vlhkost_pct ?? '',
      pevnost: record?.pevnost || '',
      poznamka: record?.poznamka || '',
      opatr_co: '', opatr_do: '', opatr_kto: '',
      prevzatie: '', odovzdanie: '',
      locs: initLocs,
    },
    fields: [],
    onSubmit: async (v) => {
      const byt = String(v.byt || '').trim()
      if (v.rozsah === 'byt' && !byt) throw new Error('Zadaj číslo bytu — zápis k bytu sa bez neho neuloží.')
      // Byt patrí celému zápisu: do blokov sa dopíše (popis prác v knihe),
      // pri zápise k stavbe sa z blokov odoberie.
      const locsOut = (v.locs || []).map(b => ({ ...b, flat: v.rozsah === 'byt' ? byt : '' }))
      const desc = buildDesc(locsOut)
      const isControl = ['prvy', 'kontrolny', 'posledny'].includes(v.entry_type)
      const opatr = isControl && v.opatr_co
        ? 'OPATRENIE: ' + v.opatr_co + (v.opatr_do ? ' · termín ' + v.opatr_do : '')
          + (v.opatr_kto ? ' · zodp. ' + v.opatr_kto : '') : ''
      const prevz = isControl
        ? [v.prevzatie ? 'Prevzaté: ' + v.prevzatie : '',
           v.odovzdanie ? 'Odovzdané: ' + v.odovzdanie : ''].filter(Boolean).join(' · ') : ''
      // DENNIK_TUK_V1: zápis na ťuk má v knihe aj „Materiál: …“ a „Poznámka: …“ — keď office bloky nezmenil, text ostáva
      const blokyRovnake = !isNew && JSON.stringify(v.locs || []) === JSON.stringify(initLocs) && v.rozsah === (record?.rozsah === 'byt' ? 'byt' : 'stavba') && byt === String(record?.byt_cislo || '').trim()
      const _pcp = projects.find(p => p.id === v.project_id)
      const _isP = ['poter', 'potery'].includes(String((_pcp && _pcp.type) || '').toLowerCase())
      const body = {
        project_id: v.project_id, entry_type: v.entry_type, date: v.date,
        weather_type: v.weather_type, worker_count: v.worker_count,
        stoppage_hours: v.stoppage_hours, stoppage_reason: v.stoppage_reason,
        stoppage_note: v.stoppage_note, koniec_etapy: v.koniec_etapy,
        stoppage_kluc: Number(v.stoppage_hours) > 0 ? v.stoppage_kluc : '', stoppage_zavinil: Number(v.stoppage_hours) > 0 ? v.stoppage_zavinil : '',
        prace_kluce: v.prace_kluce || [],
        ...(blokyRovnake ? {} : { work_description: desc, locations: locsOut,
          rozsah: v.rozsah === 'byt' ? 'byt' : 'stavba', byt_cislo: v.rozsah === 'byt' ? byt : '' }),
        temp_avg: v.temp_avg, temp_min: v.temp_avg, temp_max: v.temp_avg,
        ...(isControl && (opatr || prevz) ? { issues: opatr, notes: prevz } : {}),
        ...(_isP ? { material_name: v.material_name, m2_done: v.m2_done, hrubka_mm: v.hrubka_mm, spotreba: v.spotreba, cena_m2: v.cena_m2, vlhkost_pct: v.vlhkost_pct, pevnost: v.pevnost, poznamka: v.poznamka } : {}),
      }
      const url = isNew ? '/api/diary/project/' + v.project_id : '/api/diary/' + record.id
      const r = await fetchAuth(url, { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(body) })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.error || 'Uloženie zlyhalo.')
      }
    },
  })

  if (!open) return null
  const f = ed.values
  const readOnly = !ed.canEdit
  const isControl = ['prvy', 'kontrolny', 'posledny'].includes(f.entry_type)
  const project = projects.find(p => p.id === f.project_id)
  const isPoter = ['poter', 'potery'].includes(String((project && project.type) || '').toLowerCase()) || /potery/.test(String((project && project.remesla) || ''))   // SUPIS_JEDNOTNE_V1
  const isRD = (project?.segment || '').toLowerCase() === 'rd'
  const doClose = () => ed.requestClose(onClose)

  const setLoc = (i, k, val) => ed.set('locs', f.locs.map((b, j) => j === i ? { ...b, [k]: val } : b))
  // Prepnutie rozsahu: pri prechode na byt sa ponúkne byt zapísaný v bloku staršieho zápisu.
  const prepniRozsah = (r) => {
    if (r === f.rozsah) return
    if (r === 'byt' && !String(f.byt || '').trim()) {
      const zBloku = String(((f.locs || []).find(b => String(b.flat || '').trim()) || {}).flat || '').trim()
      if (zBloku) ed.set('byt', zBloku)
    }
    ed.set('rozsah', r)
  }
  const addLoc = () => ed.set('locs', [...f.locs, { object: '', floor: '', flat: '', room: '', text: '', done: false }])
  const [customFloor, setCustomFloor] = useState({})
  const delLoc = (i) => { if (f.locs.length > 1) ed.set('locs', f.locs.filter((_, j) => j !== i)) }

  function toConfirm() {
    if (!f.project_id) { ed.setError('Vyber zákazku.'); return }
    if (f.rozsah === 'byt' && !String(f.byt || '').trim()) {
      ed.setError('Zadaj číslo bytu — zápis k bytu sa bez neho neuloží.'); return
    }
    // Mirko 3.8.: povinná je iba zákazka — popis prác je odteraz dobrovoľný
    ed.setError(null); setStep('confirm')
  }
  async function doSave() {
    const ok = await ed.submit()
    if (ok) onSaved()
    else setStep('form')   // chyba → späť na formulár, banner ukáže ed.error
  }

  const projOpts = projects.filter(p => p.status !== 'done')
    .map(p => ({ value: p.id, label: p.name + ' (' + p.id + ')' }))

  return (
    <>
      <Modal open onClose={doClose} onDirtyClose={doClose} isDirty={ed.isDirty}
        title={isNew ? 'Nový zápis denníka' : 'Upraviť zápis — ' + fmtD(record.date)}
        subtitle={step === 'confirm' ? 'Skontroluj a potvrď uloženie' : ''}
        maxWidth={720}
        footer={step === 'form' ? (
          <>
            <BtnGhost onClick={doClose}>Zrušiť</BtnGhost>
            <BtnAdd onClick={toConfirm} disabled={readOnly}>Uložiť →</BtnAdd>
          </>
        ) : (
          <>
            <BtnGhost onClick={() => setStep('form')}>← Späť na úpravu</BtnGhost>
            <BtnAdd onClick={doSave} disabled={ed.submitting}>
              {ed.submitting ? 'Ukladám…' : '✓ Potvrdiť uloženie'}
            </BtnAdd>
          </>
        )}>

        {readOnly && ed.lockedBy && (
          <div style={banner(COLORS.remove)}>Zápis práve upravuje <strong>{ed.lockedBy}</strong>.</div>
        )}
        {ed.error && <div style={banner(COLORS.remove)}>{ed.error}</div>}

        {step === 'form' ? (
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0,
            display: 'flex', flexDirection: 'column', gap: SPACE.md }}>
            <Field label="Zákazka *">
              <SearchableSelect value={f.project_id} onChange={v => ed.set('project_id', v)}
                options={projOpts} placeholder="Hľadať zákazku…" />
            </Field>
            <div style={{ display: 'flex', gap: SPACE.md }}>
              <Field label="Typ zápisu" flex>
                <SearchableSelect value={f.entry_type} onChange={v => ed.set('entry_type', v)}
                  options={types.map(t => ({ value: t, label: TYPES[t]?.label || t }))} />
              </Field>
              <Field label="Dátum" flex>
                <input type="date" value={f.date} onChange={e => ed.set('date', e.target.value)} style={inp} />
              </Field>
            </div>
            {/* ROZSAH ZÁPISU — k stavbe alebo k jednému bytu (Mirko 28. 9.) */}
            <div style={{ display: 'flex', gap: SPACE.md, alignItems: 'flex-end' }}>
              <Field label="Rozsah zápisu" flex>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" onClick={() => prepniRozsah('stavba')}
                    style={rozsahBtn(f.rozsah !== 'byt')}>K stavbe</button>
                  <button type="button" onClick={() => prepniRozsah('byt')}
                    style={rozsahBtn(f.rozsah === 'byt')}>K bytu</button>
                </div>
              </Field>
              {f.rozsah === 'byt' ? (
                <Field label="Byt *" flex>
                  <input placeholder="napr. B3.12" value={f.byt}
                    onChange={e => ed.set('byt', e.target.value)} style={inp} />
                </Field>
              ) : (
                <Field label="" flex>
                  <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, padding: '7px 0' }}>
                    Celá stavba, objekt alebo poschodie — bez bytu.
                  </div>
                </Field>
              )}
            </div>
            <div style={{ display: 'flex', gap: SPACE.md }}>
              <Field label="Počasie" flex>
                <SearchableSelect value={f.weather_type} onChange={v => ed.set('weather_type', v)}
                  emptyLabel="—" options={WEATHER.map(w => ({ value: w, label: WEATHER_LABEL[w] }))} />
              </Field>
              <Field label="Teplota °C" flex>
                <input type="number" value={f.temp_avg}
                  onChange={e => ed.set('temp_avg', e.target.value)} style={inp} />
              </Field>
              <Field label="Pracovníci" flex>
                <input type="number" min={1} value={f.worker_count}
                  onChange={e => ed.set('worker_count', e.target.value)} style={inp} />
              </Field>
            </div>

            {/* popis prác — multi-blok lokácie (FIRMA/RD per segment) */}
            <div>
              <div style={fieldLbl}>Popis prác  {project && (
                <span style={{ color: COLORS.muted, textTransform: 'none' }}>
                  · {isRD ? 'RD' : 'FIRMA'}</span>)}</div>
              {f.locs.map((b, i) => (
                <div key={i} style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md,
                  padding: SPACE.sm, marginBottom: 6 }}>
                  <div style={{ display: 'flex', gap: SPACE.sm, marginBottom: 6 }}>
                    {!isRD && <input placeholder="Objekt" value={b.object}
                      onChange={e => setLoc(i, 'object', e.target.value)} style={inp} />}
                    <select style={inp}
                      value={(customFloor[i] || (b.floor && !FLOORS.includes(b.floor))) ? '__ine' : b.floor}
                      onChange={e => { const v = e.target.value
                        if (v === '__ine') { setCustomFloor(s => ({ ...s, [i]: true })); setLoc(i, 'floor', '') }
                        else { setCustomFloor(s => ({ ...s, [i]: false })); setLoc(i, 'floor', v) } }}>
                      <option value="">Poschodie</option>
                      {FLOORS.map(fl => <option key={fl} value={fl}>{fl}</option>)}
                      <option value="__ine">Iné…</option>
                    </select>
                    <input placeholder={isRD ? 'Miestnosť (nepov.)' : 'Miestnosť'}
                      value={b.room} onChange={e => setLoc(i, 'room', e.target.value)} style={inp} />
                  </div>
                  {(customFloor[i] || (b.floor && !FLOORS.includes(b.floor))) && (
                    <input placeholder="Vlastný popis poschodia/miesta…" value={b.floor}
                      onChange={e => setLoc(i, 'floor', e.target.value)} style={{ ...inp, marginBottom: 6 }} />
                  )}
                  <textarea placeholder="Čo sa robilo…" value={b.text} rows={2}
                    onChange={e => setLoc(i, 'text', e.target.value)}
                    style={{ ...inp, resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: SPACE.sm, marginTop: 4 }}>
                    <label style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted,
                      display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="checkbox" checked={!!b.done}
                        onChange={e => setLoc(i, 'done', e.target.checked)} /> miestnosť hotová
                    </label>
                    <span style={{ flex: 1 }} />
                    {f.locs.length > 1 && <BtnGhost onClick={() => delLoc(i)}>− Odstrániť</BtnGhost>}
                  </div>
                </div>
              ))}
              <BtnGhost onClick={addLoc}>+ Pridať popis</BtnGhost>
            </div>

            {/* kontrolný — opatrenie + prevzatie */}
            {isPoter && (
              <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.md, background: COLORS.bgSoft }}>
                <div style={fieldLbl}>Poter — údaje (oprava chybného mobilného zadania)</div>
                <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' }}>
                  <Field label="Materiál" flex><input value={f.material_name} onChange={e => ed.set('material_name', e.target.value)} style={inp} /></Field>
                  <Field label="Plocha m²" flex><input type="number" step="0.01" value={f.m2_done} onChange={e => ed.set('m2_done', e.target.value)} style={inp} /></Field>
                  <Field label="Hrúbka mm" flex><input type="number" value={f.hrubka_mm} onChange={e => ed.set('hrubka_mm', e.target.value)} style={inp} /></Field>
                </div>
                <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap', marginTop: 6 }}>
                  <Field label="Spotreba m³" flex><input type="number" step="0.01" value={f.spotreba} onChange={e => ed.set('spotreba', e.target.value)} style={inp} /></Field>
                  <Field label="Cena €/m²" flex><input type="number" step="0.01" value={f.cena_m2} onChange={e => ed.set('cena_m2', e.target.value)} style={inp} /></Field>
                  <Field label="Vlhkosť %" flex><input type="number" step="0.1" value={f.vlhkost_pct} onChange={e => ed.set('vlhkost_pct', e.target.value)} style={inp} /></Field>
                  <Field label="Pevnosť" flex><input value={f.pevnost} onChange={e => ed.set('pevnost', e.target.value)} style={inp} /></Field>
                </div>
                <div style={{ marginTop: 6 }}>
                  <Field label="Poznámka (dilatácie, prídavky…)"><input value={f.poznamka} onChange={e => ed.set('poznamka', e.target.value)} style={inp} /></Field>
                </div>
              </div>
            )}
            {isControl && (
              <>
                <Field label="Uložené opatrenie (čo / termín / kto)">
                  <input placeholder="Čo treba spraviť" value={f.opatr_co}
                    onChange={e => ed.set('opatr_co', e.target.value)} style={{ ...inp, marginBottom: 6 }} />
                  <div style={{ display: 'flex', gap: SPACE.sm }}>
                    <input type="date" value={f.opatr_do}
                      onChange={e => ed.set('opatr_do', e.target.value)} style={inp} />
                    <input placeholder="Zodpovedný" value={f.opatr_kto}
                      onChange={e => ed.set('opatr_kto', e.target.value)} style={inp} />
                  </div>
                </Field>
                <div style={{ display: 'flex', gap: SPACE.md }}>
                  <Field label="Prevzaté od zhotoviteľa" flex>
                    <input value={f.prevzatie} onChange={e => ed.set('prevzatie', e.target.value)} style={inp} />
                  </Field>
                  <Field label="Odovzdané zhotoviteľovi" flex>
                    <input value={f.odovzdanie} onChange={e => ed.set('odovzdanie', e.target.value)} style={inp} />
                  </Field>
                </div>
              </>
            )}

            {/* prestoje — DENNIK_TUK_V1: dôvod z katalógu denníka, kto zavinil ťukom (ústava 25.1) */}
            <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.sm, background: '#FFFBEB' }}>
              <div style={{ ...fieldLbl, color: COLORS.warn }}>Prestoj</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                {[0, 0.5, 1, 2, 4, 8].map(h => (
                  <button key={h} type="button" onClick={() => ed.set('stoppage_hours', h)} style={prestojBtn(Number(f.stoppage_hours || 0) === h)}>
                    {h === 0 ? 'Bez prestoja' : h === 8 ? 'celý deň' : h === 0.5 ? '½ h' : h + ' h'}
                  </button>
                ))}
                <input type="number" step="0.5" min="0" value={f.stoppage_hours} title="Iný počet hodín"
                  onChange={e => ed.set('stoppage_hours', e.target.value)} style={{ ...inp, width: 80 }} />
              </div>
              {Number(f.stoppage_hours) > 0 && (
                <div style={{ display: 'flex', gap: SPACE.md, flexWrap: 'wrap' }}>
                  <Field label="Dôvod prestoja" flex>
                    <SearchableSelect value={f.stoppage_kluc || (f.stoppage_reason ? '__text' : '')}
                      onChange={v => { const p = (kat?.prestoje || []).find(x => x.kluc === v)
                        if (p) { ed.set('stoppage_kluc', p.kluc); ed.set('stoppage_reason', vetaPrestoja(p)); if (!f.stoppage_zavinil && p.kto_zavinil) { ed.set('stoppage_zavinil', p.kto_zavinil); ed.set('stoppage_note', 'zavinil ' + zavinilMale(kat, p.kto_zavinil)) } }
                        else if (!v) { ed.set('stoppage_kluc', ''); ed.set('stoppage_reason', '') } }}
                      emptyLabel="—" options={[
                        ...((f.stoppage_reason && !f.stoppage_kluc) ? [{ value: '__text', label: f.stoppage_reason + ' (starý zápis)' }] : []),
                        ...(kat?.prestoje || []).map(p => ({ value: p.kluc, label: p.text + (p.fotka_povinna ? ' — fotka povinná' : '') })),
                        ...(kat ? [] : STOPPAGE_REASONS.map(r => ({ value: r, label: r }))),
                      ]} />
                  </Field>
                  <Field label="Kto zavinil" flex>
                    <div style={{ display: 'flex', gap: 6 }}>{(kat?.zavinil || []).map(z => (
                      <button key={z.kluc} type="button" style={prestojBtn(f.stoppage_zavinil === z.kluc)}
                        onClick={() => { ed.set('stoppage_zavinil', z.kluc); ed.set('stoppage_note', 'zavinil ' + zavinilMale(kat, z.kluc)) }}>{z.text}</button>
                    ))}</div>
                  </Field>
                </div>
              )}
            </div>
            {/* DENNIK_TUK_V1: práce z katalógu — ťuk pridá text do prvého popisu (rovnaké dlaždice ako mview a appky) */}
            {kat && !isControl && (
              <div>
                <div style={fieldLbl}>Práce z katalógu (ťuk pridá do popisu)</div>
                {[...(kat.sady || []), ...(kat.ine_remesla || [])].map(s => (
                  <div key={s.sada} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: COLORS.muted, textTransform: 'uppercase', width: 96 }}>{s.nazov}</span>
                    {s.polozky.map(p => {
                      const on = (f.prace_kluce || []).includes(p.kluc)
                      return <button key={p.kluc} type="button" style={prestojBtn(on, COLORS.nav)} onClick={() => {
                        if (on) {   // odznačenie vráti aj text z popisu
                          ed.set('prace_kluce', f.prace_kluce.filter(x => x !== p.kluc))
                          const t0 = String((f.locs[0] || {}).text || '')
                          setLoc(0, 'text', t0.split(', ').filter(x => x !== p.text).join(', '))
                          return
                        }
                        ed.set('prace_kluce', [...(f.prace_kluce || []), p.kluc])
                        const t0 = String((f.locs[0] || {}).text || '')
                        setLoc(0, 'text', t0 ? t0 + ', ' + p.text : p.text)
                      }}>{p.text}</button>
                    })}
                  </div>
                ))}
              </div>
            )}

            {/* koniec etapy — DENNIK_TUK_V1: Mirko 29. 9. 2026 (matrix 1009) „výpltata je už daná - denník vynecháme z ustavy“ — prepínač preč */}
            {false && f.entry_type === 'denny' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: SPACE.sm,
                padding: SPACE.sm, borderRadius: RADIUS.md, border: `2px solid ${f.koniec_etapy
                  ? COLORS.total : COLORS.line}`, background: COLORS.totalBg, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!f.koniec_etapy}
                  onChange={e => ed.set('koniec_etapy', e.target.checked ? 1 : 0)} />
                <span style={{ fontWeight: TYPO.weights.bold, color: COLORS.total }}>
                  KONČÍM ETAPU — ŽIADAM VÝPLATU
                </span>
              </label>
            )}
          </fieldset>
        ) : (
          // ── CONFIRM ──
          <div style={{ fontSize: TYPO.sizes.base, lineHeight: 1.8 }}>
            <p>{isNew ? 'Vytvorí sa nový zápis:' : 'Uložia sa zmeny zápisu:'}</p>
            <DRow l="Zákazka" v={project?.name || f.project_id} />
            <DRow l="Typ" v={TYPES[f.entry_type]?.label} />
            <DRow l="Rozsah" v={f.rozsah === 'byt' ? 'Zápis k bytu ' + String(f.byt || '').trim() : 'Zápis k stavbe'} />
            <DRow l="Dátum" v={fmtD(f.date)} />
            <DRow l="Popis prác" v={buildDesc((f.locs || []).map(b => ({ ...b, flat: f.rozsah === 'byt' ? String(f.byt || '').trim() : '' })))} />

          </div>
        )}
      </Modal>
      <Modal.DirtyConfirm {...ed.dirtyProps} />
    </>
  )
}

const inp = {
  width: '100%', padding: '6px 10px', fontSize: TYPO.sizes.base, fontFamily: 'inherit',
  border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, outline: 'none', boxSizing: 'border-box',
}
// prepínač rozsahu zápisu — zvolená strana má farbu modulu
function rozsahBtn(on) {
  return {
    flex: 1, padding: '6px 10px', fontSize: TYPO.sizes.base, fontFamily: 'inherit',
    fontWeight: on ? TYPO.weights.bold : TYPO.weights.semibold, cursor: 'pointer',
    border: `1px solid ${on ? COLORS.nav : COLORS.line}`, borderRadius: RADIUS.md,
    background: on ? TINT.nav : COLORS.bg, color: on ? COLORS.nav : COLORS.muted,
  }
}
const fieldLbl = {
  fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.semibold, color: COLORS.muted,
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
}
function Field({ label, children, flex }) {
  return (
    <label style={{ display: 'block', flex: flex ? 1 : 'none', minWidth: 0 }}>
      <div style={fieldLbl}>{label}</div>
      {children}
    </label>
  )
}
function banner(c) {
  return { background: TINT.remove, border: `1px solid ${c}`, borderRadius: RADIUS.md,
    padding: SPACE.sm, marginBottom: SPACE.md, fontSize: TYPO.sizes.sm, color: c }
}

// ── DENNIK_TUK_V1: pomocné pre katalóg denníka (texty sú slovenské — desktop je po slovensky, ústava 6) ──
function jsonPole(x) { if (Array.isArray(x)) return x; try { const a = JSON.parse(x || '[]'); return Array.isArray(a) ? a : [] } catch (_) { return [] } }
function textKatalogu(kat, k) {
  if (!kat) return k
  for (const s of [...(kat.sady || []), ...(kat.ine_remesla || []), ...(kat.kontrola || [])]) { const p = (s.polozky || []).find(x => x.kluc === k); if (p) return p.text }
  const p = (kat.prestoje || []).find(x => x.kluc === k); return p ? p.text : k
}
function zavinilText(kat, k) { const z = (kat?.zavinil || []).find(x => x.kluc === k); return z ? z.text : '' }
function zavinilMale(kat, k) { const t = zavinilText(kat, k); return k === 'kamir' ? t : t.toLowerCase() }
function vetaPrestoja(p) { const v = String(p.veta || p.text || ''); return v.charAt(0).toUpperCase() + v.slice(1) }
function prestojBtn(on, farba) {
  const c = farba || COLORS.warn
  return { padding: '5px 10px', fontSize: TYPO.sizes.sm, fontFamily: 'inherit', fontWeight: on ? TYPO.weights.bold : TYPO.weights.semibold, cursor: 'pointer',
    border: `1px solid ${on ? c : COLORS.line}`, borderRadius: 999, background: on ? c : COLORS.bg, color: on ? '#fff' : COLORS.text, whiteSpace: 'nowrap' }
}
